import axios from 'axios';
import { authService } from './authService';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api/v1';

// Access token storage key
const ACCESS_TOKEN_STORAGE_KEY = '@KatruFront:access_token';

// Create axios instance
const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 15000, // 15 seconds
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
});

// Request interceptor to add auth token and handle token refresh
api.interceptors.request.use(
    async (config: any) => {
        // Skip token check for auth endpoints
        if (config.url?.includes('/auth/')) {
            return config;
        }

        // Get current token and add to headers (without checking expiry)
        const currentToken = apiService.getAuthToken();
        if (currentToken && config.headers) {
            config.headers.Authorization = `Bearer ${currentToken}`;
        }

        return config;
    },
    (error: any) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle common errors
api.interceptors.response.use(
    (response: any) => {
        return response;
    },
    async (error: any) => {
        const originalRequest = error.config as any & { _retry?: boolean };

        // Handle 403 Forbidden errors (token expired)
        if (error.response?.status === 403 && !originalRequest._retry) {
            // If the request is to refresh-token endpoint, logout directly
            if (originalRequest.url?.includes('/auth/refresh-token')) {
                await authService.logout();
                return Promise.reject(error);
            }

            originalRequest._retry = true;

            // Try to refresh the token
            try {
                const refreshResponse = await authService.refreshToken();

                // Retry the original request with the new token
                originalRequest.headers.Authorization = `Bearer ${refreshResponse.access_token}`;
                return api(originalRequest);
            } catch (refreshError) {
                // Refresh failed, logout the user
                await authService.logout();
                return Promise.reject(error);
            }
        }

        // Handle 401 Unauthorized errors (permission denied, not token expired)
        if (error.response?.status === 401) {
            console.error('Unauthorized. You may not have permission to access this resource.');
            // You can show a toast notification here
        }

        // Handle 500 Server errors
        if (error.response?.status >= 500) {
            console.error('Server error occurred. Please try again later.');
            // You can show a toast notification here
        }

        return Promise.reject(error);
    }
);

// API Service class with common HTTP methods
export class ApiService {
    private static instance: ApiService;
    private api: any;

    private constructor() {
        this.api = api;
    }

    public static getInstance(): ApiService {
        if (!ApiService.instance) {
            ApiService.instance = new ApiService();
        }
        return ApiService.instance;
    }

    // GET request
    async get<T = any>(url: string, config?: any): Promise<T> {
        try {
            const response = await this.api.get(url, config);
            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    // POST request
    async post<T = any>(url: string, data?: any, config?: any): Promise<T> {
        try {
            const response = await this.api.post(url, data, config);
            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    // PUT request
    async put<T = any>(url: string, data?: any, config?: any): Promise<T> {
        try {
            const response = await this.api.put(url, data, config);
            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    // PATCH request
    async patch<T = any>(url: string, data?: any, config?: any): Promise<T> {
        try {
            const response = await this.api.patch(url, data, config);
            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    // DELETE request
    async delete<T = any>(url: string, config?: any): Promise<T> {
        try {
            const response = await this.api.delete(url, config);
            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    // File upload
    async upload<T = any>(url: string, file: File, config?: any): Promise<T> {
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await this.api.post(url, formData, {
                ...config,
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    // Multiple files upload
    async uploadMultiple<T = any>(url: string, files: File[], config?: any): Promise<T> {
        try {
            const formData = new FormData();
            files.forEach((file, index) => {
                formData.append(`files[${index}]`, file);
            });

            const response = await this.api.post(url, formData, {
                ...config,
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    // Set access token
    setAuthToken(accessToken: string): void {
        localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);
    }

    // Get access token
    getAuthToken(): string | null {
        return localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
    }

    // Remove access token
    removeAuthToken(): void {
        localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
    }

    // Check if user is authenticated
    isAuthenticated(): boolean {
        return !!this.getAuthToken();
    }

    // Error handler
    private handleError(error: any): void {
        if (error && error.isAxiosError) {
            const axiosError = error;

            // Log error details
            console.error('API Error:', {
                status: axiosError.response?.status,
                statusText: axiosError.response?.statusText,
                url: axiosError.config?.url,
                method: axiosError.config?.method,
                data: axiosError.response?.data,
                message: axiosError.message,
            });

            // You can add custom error handling here
            // For example, show toast notifications, redirect, etc.
        } else {
            console.error('Non-Axios Error:', error);
        }
    }
}

// Export singleton instance
export const apiService = ApiService.getInstance();

// Export the axios instance for direct use if needed
export { api };

// Export types for better TypeScript support
export interface ApiResponse<T = any> {
    data: T;
    message?: string;
    success: boolean;
}

export interface PaginatedResponse<T = any> {
    data: T[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

// Common error types
export interface ApiError {
    message: string;
    code?: string;
    details?: any;
}

// Export default for convenience
export default apiService; 